// Pure helpers for the combined-contact issue's decide flow (Donna
// 2026-07-30). "Looks like two people in one contact — split or remove
// it?" used to recommend opening the contact record, where NO split
// affordance exists — a dead end (UX rule #5): "I have opened up the
// contact but there's no indication of where I can split or remove
// someone." The two honest answers are now one click in the rail:
//
//   verify — "Split into two contacts": the backend re-derives the two
//     people from the STORED name (recase doctrine), renames the
//     existing record to person 1 (keeping its email/phone/history) and
//     creates person 2 as a new contact in the same household. 60s
//     undo; the rename stays reversible from History after.
//   archive — "Archive this combined record": the right answer when
//     both people already exist as their own contacts (the detector
//     says so via its recommendation). Soft + restorable.
//
// Root-safe (imports only the shared pure name parser) so it's
// unit-tested from tests/web/.

import { splitCombinedNamePair } from "../../../shared/lib/multi-person-name";

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface CombinedContactIssueLike {
  detectorSlug: string;
  affectedRecords: Array<{ kind: string; id: string; label: string }>;
  recommendation: { verb: string; label: string } | null;
}

export function isCombinedContactIssue(
  issue: CombinedContactIssueLike,
): boolean {
  return issue.detectorSlug === "combined-contact";
}

// The detector recommends archiving (rather than splitting) when both
// people already exist as their own contacts in the household. Matched
// on the label keyword so issues materialized under the old copy
// ("Open & archive (redundant combined record)") behave the same as
// new ones ("Archive the redundant combined record").
export function combinedContactPrefersArchive(
  issue: CombinedContactIssueLike,
): boolean {
  return /redundant/i.test(issue.recommendation?.label ?? "");
}

// Button copy naming the two people when the combined name parses
// cleanly ("Split into “Carie D'Hondt” + “Eddie D'Hondt”"); generic
// otherwise — the backend does its own strict parse either way and
// answers with a plain "fix it by hand" message when it can't.
export function combinedContactSplitLabel(
  issue: CombinedContactIssueLike,
): string {
  const combined =
    issue.affectedRecords.find((r) => r.kind === "contact")?.label ?? "";
  const pair = splitCombinedNamePair(combined);
  if (pair) {
    const nameOf = (p: { firstName: string; lastName: string | null }) =>
      [p.firstName, p.lastName].filter(Boolean).join(" ");
    return `Split into “${nameOf(pair[0])}” + “${nameOf(pair[1])}”`;
  }
  return "Split into two contacts";
}

export function combinedContactSplitConsequence(): string {
  return "The existing record is renamed to the first person — it keeps its email, phone, household and history. The second person becomes a new contact in the same household (name only, so nothing is duplicated). Reversible for 60 seconds, and the rename stays reversible from History after.";
}

export const COMBINED_CONTACT_ARCHIVE_LABEL =
  "Archive this combined record";

export function combinedContactArchiveConsequence(
  prefersArchive: boolean,
): string {
  return prefersArchive
    ? "Both people already exist as their own contacts in this household, so this combined record is redundant — archiving removes it from every list while keeping the row and a version snapshot. Reversible for 60 seconds, and restorable any time after."
    : "Archives the combined record — removed from every list, but the row and a version snapshot are kept, so it's fully restorable. Use this only if the two people already exist separately (or it's not a real person); otherwise split it so neither person is lost.";
}
